import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { animeList } from '@/mocks/anime';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);
  const [activeTab, setActiveTab] = useState<'intro' | 'episodes'>('intro');

  if (!anime) {
    return (
      <>
        <title>找不到该动漫 - 樱花动漫</title>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">找不到该动漫</h1>
            <p className="text-foreground-500 mb-6">该动漫可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedAnime = animeList
    .filter((a) => a.categorySlug === anime.categorySlug && a.id !== anime.id)
    .slice(0, 4);

  const pageTitle = `${anime.title} - ${anime.subtitle} - 樱花动漫高清在线观看`;
  const pageDesc = `${anime.description} ${anime.longDescription.substring(0, 120)}`;
  const pageKeywords = `樱花动漫,${anime.title},${anime.subtitle},${anime.category}动漫,${anime.tags.join(',')}`;

  const faqSchema = {
    mainEntity: [
      {
        '@type': 'Question',
        name: `${anime.title}是什么类型的动漫？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${anime.title}是一部${anime.category}类动漫，由${anime.studio}制作，${anime.director}导演，于${anime.year}年播出。`,
        },
      },
      {
        '@type': 'Question',
        name: `${anime.title}有多少集？完结了吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${anime.title}共${anime.episodes}话，目前状态为${anime.status}。`,
        },
      },
      {
        '@type': 'Question',
        name: `如何在线观看${anime.title}高清动漫？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在樱花动漫网免费在线观看${anime.title}，无需注册或订阅，高清画质流畅播放。`,
        },
      },
    ],
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://www.sakura-anime.com/article/${anime.id}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={anime.coverImage} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.sakura-anime.com/article/${anime.id}`} />
      <meta property="og:site_name" content="樱花动漫" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={anime.coverImage} />

      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Article" data={{
        headline: `${anime.title} - ${anime.category}动漫`,
        description: anime.description,
        image: anime.coverImage,
        datePublished: `${anime.year}-01-01`,
        dateModified: anime.updateDate,
        author: {
          '@type': 'Organization',
          name: '樱花动漫',
        },
        publisher: {
          '@type': 'Organization',
          name: '樱花动漫',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.sakura-anime.com/article/${anime.id}`,
        },
      }} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <SchemaOrg type="WebPage" data={{
        url: `https://www.sakura-anime.com/article/${anime.id}`,
        name: `${anime.title} - 樱花动漫详情`,
        description: pageDesc,
      }} />

      <ScrollToTop />
      <Navbar />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: anime.category, href: `/category/${anime.categorySlug}` },
              { label: anime.title },
            ]} />
          </div>
        </div>

        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={anime.bannerImage}
            alt={anime.title}
            title={`${anime.title} - 樱花动漫`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-background-100"></div>
        </section>

        <section className="pb-14 md:pb-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-64 flex-shrink-0 -mt-24 md:-mt-32 relative z-10">
                <div className="w-48 md:w-56 aspect-[2/3] rounded-xl overflow-hidden mx-auto lg:mx-0">
                  <img
                    src={anime.coverImage}
                    alt={anime.title}
                    title={`${anime.title} - ${anime.category}动漫`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 pt-2">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full font-medium">
                    {anime.category}
                  </span>
                  {anime.status === '连载中' && (
                    <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full font-medium">
                      {anime.status}
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {anime.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{anime.subtitle}</p>

                <div className="flex items-center gap-4 mb-6 flex-wrap">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{anime.rating}</span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{anime.episodes} 话</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{anime.year}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{(anime.views / 10000).toFixed(0)} 万次观看</span>
                </div>

                <div className="flex gap-3 mb-8">
                  <button
                    type="button"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm flex items-center gap-2"
                  >
                    <i className="ri-play-circle-line"></i>
                    开始观看
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-50 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    aria-label="收藏"
                  >
                    <i className="ri-heart-line text-foreground-600"></i>
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-50 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    aria-label="分享"
                  >
                    <i className="ri-share-line text-foreground-600"></i>
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-50 text-foreground-600 border border-background-200/70">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-1 mb-6 border-b border-background-200/70">
                  <button
                    type="button"
                    onClick={() => setActiveTab('intro')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === 'intro'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    动漫介绍
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('episodes')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === 'episodes'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    剧集列表
                  </button>
                </div>

                {activeTab === 'intro' ? (
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-8">
                      {anime.longDescription}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-5 rounded-xl bg-background-50 border border-background-200/70">
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">制作公司</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.studio}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">导演</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.director}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">年份</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.year}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">状态</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.status}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">总集数</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.episodes} 话</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">评分</p>
                        <p className="text-sm font-medium text-foreground-900">{anime.rating} / 10</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Array.from({ length: anime.episodes }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        className="px-4 py-3 rounded-lg border border-background-200/70 bg-background-50 hover:bg-primary-50 hover:border-primary-200 transition-all duration-200 text-sm text-foreground-700 hover:text-primary-600 text-center cursor-pointer"
                      >
                        第 {i + 1} 话
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                相关推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">更多{anime.category}类动漫推荐</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {relatedAnime.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      title={`${item.title} - 樱花动漫推荐`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      {item.rating}
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.episodes} 话 · {item.year}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
            </div>
            <div className="max-w-3xl space-y-3">
              {faqSchema.mainEntity.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900">{faq.name}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}